/**
 * In-app alert for a freshly-arrived group or direct message, web-only.
 *
 * With both mute toggles off, no notification showed
 * for an incoming message. The mute settings themselves were fine
 * (`useChatPreferences`, `sdk-server/src/push/mute.ts`); the gap was that web
 * has no notification path of its own. The only push is Firebase, and the
 * server skips it for any device it considers online
 * (`sdk-server/src/push/service.ts`), so a user with the tab open never got
 * anything; the live channel subscription just updated the store silently.
 *
 * Mount this once, alongside `useMessengerSubscription` (both live in
 * `DashboardLayout` via `useConversationList`, so both run app-wide, not only
 * while the Presence page is mounted). That is what lets a message alert work
 * while the user is on /meetings, /settings, anywhere.
 *
 *   useIncomingMessageAlerts()
 *
 * The decision of whether one message should alert is `shouldAlert`, exported
 * pure so it can be unit-tested without a store, a DOM, or React at all.
 */

import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/features/auth/store/auth.store'
import { useChatPreferences } from './useChatPreferences'
import { useMessengerStore } from '../store/messenger.store'
import { subscribeIncomingMessages } from '../services/incoming-message-bus'
import { getActiveConversationId } from '../store/active-conversation'
import type {
  Message as WireMessage,
  Conversation as WireConversation,
} from '../services/messenger.wire'
import type { ChatPreferences } from '@/sdk/instavcMessenger'
import { toast } from '@/clientWeb/components/ui/toast-utils'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'

/** Bursts collapse to one alert per conversation in this window. */
const THROTTLE_MS = 3_000

/** Same render hint the chat-list tile uses (`ATTACHMENT_LABEL` in
 *  `messenger.store.ts`), translated here since this text is user-facing on
 *  its own (a toast or a browser notification), unlike the tile preview. */
const ATTACHMENT_KEY: Record<string, string> = {
  photo: 'presence.attachmentPhoto',
  video: 'presence.attachmentVideo',
  audio: 'presence.attachmentAudio',
  voice: 'presence.attachmentVoice',
  document: 'presence.attachmentDocument',
}

export interface ShouldAlertMessage {
  type: WireMessage['type']
  senderId: WireMessage['senderId']
  conversationId: WireMessage['conversationId']
}

export interface ShouldAlertConversation {
  kind: WireConversation['kind']
  participants?: { userId: string; muted: boolean }[]
}

export interface ShouldAlertInput {
  message: ShouldAlertMessage
  myId: string | null
  /** The conversation whose thread is actually mounted on screen, or null. */
  openConversationId: string | null
  /** `!document.hidden` at the moment the message arrived. */
  pageVisible: boolean
  /** Undefined when the conversation row hasn't loaded yet; never alert on a
   *  conversation the client doesn't have a row for. */
  conversation: ShouldAlertConversation | undefined
  prefs: Pick<ChatPreferences, 'muteGroups' | 'muteDirect'>
  /** False until the account's mute preferences have come back from the
   *  server at least once. Before that `prefs` is just `DEFAULTS`
   *  (unmuted), which would alert through a mute the user already has on. */
  prefsLoaded: boolean
}

/**
 * Pure decision of whether one live `message:new` event should alert the
 * user. No DOM, no store reads, no i18n: everything it needs is passed in.
 */
export function shouldAlert(input: ShouldAlertInput): boolean {
  const { message, myId, openConversationId, pageVisible, conversation, prefs, prefsLoaded } = input

  // Only a real text message alerts. System rows (membership/rename/etc.) and
  // call-log entries reach `message:new` the same as text does, but neither
  // is something to interrupt the user about. Edits and reactions never reach
  // this function at all: the bus only republishes `message:new`, never
  // `message:updated` or a reaction event.
  if (message.type !== 'text') return false
  if (!message.senderId || message.senderId === myId) return false
  if (!conversation) return false

  // The thread is right there on screen, already seen, no need to alert.
  if (pageVisible && openConversationId === message.conversationId) return false

  const iAmMuted = conversation.participants?.find((p) => p.userId === myId)?.muted ?? false
  if (iAmMuted) return false

  // Until the account prefs have loaded once, muteGroups/muteDirect sit at
  // their unmuted defaults, so trusting them here would alert through a mute
  // the user already has on. The per-chat mute above is unaffected by this:
  // it comes from the conversation row, not these preferences.
  if (!prefsLoaded) return false

  if (conversation.kind === 'group' && prefs.muteGroups) return false
  if (conversation.kind === 'dm' && prefs.muteDirect) return false

  return true
}

function previewFor(message: WireMessage, t: (key: string) => string): string {
  if (message.body) return message.body
  const kind = message.attachments?.[0]?.kind
  const key = kind ? ATTACHMENT_KEY[kind] : undefined
  return key ? t(key) : ''
}

export function useIncomingMessageAlerts(): void {
  const t = useTranslation()
  const navigate = useNavigate()
  const myId = useAuthStore((s) => s.presenceId)
  const { preferences, preferencesLoaded } = useChatPreferences()

  // The bus callback below is registered once; it reads these refs rather
  // than closed-over values so it always sees the LATEST id/prefs without
  // having to resubscribe on every change.
  const myIdRef = useRef(myId)
  myIdRef.current = myId
  const prefsRef = useRef(preferences)
  prefsRef.current = preferences
  const prefsLoadedRef = useRef(preferencesLoaded)
  prefsLoadedRef.current = preferencesLoaded

  const pageVisibleRef = useRef(typeof document === 'undefined' ? true : !document.hidden)
  useEffect(() => {
    const onVisibility = () => { pageVisibleRef.current = !document.hidden }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  // conversationId -> last alert epoch ms. Module-local to this hook instance
  // (one mount, app-wide) so it survives for the life of the session.
  const lastAlertAtRef = useRef<Map<string, number>>(new Map())

  useEffect(() => {
    return subscribeIncomingMessages((message) => {
      const myIdNow = myIdRef.current
      if (!myIdNow) return

      const conversation = useMessengerStore.getState().wireConversations.get(message.conversationId)

      if (!shouldAlert({
        message,
        myId: myIdNow,
        openConversationId: getActiveConversationId(),
        pageVisible: pageVisibleRef.current,
        conversation,
        prefs: prefsRef.current,
        prefsLoaded: prefsLoadedRef.current,
      })) {
        return
      }

      const now = Date.now()
      const last = lastAlertAtRef.current.get(message.conversationId) ?? 0
      if (now - last < THROTTLE_MS) return
      lastAlertAtRef.current.set(message.conversationId, now)

      const senderName =
        conversation?.participants?.find((p) => p.userId === message.senderId)?.name ?? ''
      const title = conversation?.kind === 'group'
        ? t('presence.newGroupMessageFrom', { sender: senderName, group: conversation.title ?? '' })
        : senderName
      const body = previewFor(message, t)

      const openThisConversation = () => {
        // PresencePage only reads `?c=` once, at mount (and otherwise listens
        // for a service-worker postMessage this hook has no way to emit). A
        // soft navigation while already sitting on /chats would just change
        // the URL and nothing else, so that one case falls back to a full
        // reload, the same `/chats?c=<id>` contract the push service worker
        // already uses for "no tab open".
        if (window.location.pathname.startsWith('/chats')) {
          window.location.href = `/chats?c=${message.conversationId}`
        } else {
          navigate(`/chats?c=${message.conversationId}`)
        }
      }

      if (pageVisibleRef.current) {
        toast.info({
          title,
          description: body || undefined,
          action: { label: t('presence.openChat'), onClick: openThisConversation },
        })
        return
      }

      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          const n = new Notification(title, {
            body,
            icon: '/favicon.svg',
            tag: message.conversationId,
          })
          n.onclick = () => {
            window.focus()
            openThisConversation()
            n.close()
          }
        } catch {
          // Best-effort: some embedders throw on `new Notification(...)`
          // even with permission granted; never worth breaking the
          // subscription over a missed alert.
        }
      }
    })
    // `navigate` is stable per react-router; `t` changes only on a language
    // switch, which is rare enough that resubscribing is cheap. `myId` and
    // `preferences` are read through refs on purpose, see above.
  }, [navigate, t])
}
